/****************
 * ACCOUNT TIERS. Guillaume, 2026-09-26.
 *
 *   1 Free      Dashboard, Traders, Strategy Builder, Copy Trade (paper only),
 *               My Sessions, Learn
 *   2 Telegram  Free + Telegram sessions
 *   3 Regular   Telegram + API keys and live trading + Community + AI Assistant
 *               + unlimited Orb
 *   4 Premium   Regular + AI Trader + order priority
 *   5 Admin     Premium + Activity (the admin dashboard)
 *
 * Backtesting is free for everybody. The list itself is tiers.json, shared
 * byte-for-byte with the server (backend/internal/tiers).
 *
 * NOT SWITCHED ON. Nobody is locked out of anything today: the paywall is
 * "infrastructure ready" only. The one place tiers act is an admin's lens --
 * the switch on the Admin badge in the header -- which shows the app the way a
 * tier would see it. A locked feature stays VISIBLE and cannot be used
 * (Guillaume: "locked features must be visible, just unusable").
 ****************/

type TierFile = {
  tiers: string[];
  features: Record<string, string>;
  orb_daily_questions: Record<string, number | null | string>;
};
// Glob rather than a JSON import: the project does not turn on resolveJsonModule.
const file = (Object.values(import.meta.glob('./tiers.json', { eager: true })) as { default: TierFile }[])[0].default;

// Privileged (Guillaume 10-25): the people who supported us from the beginning; Premium, never billed.
export const TIERS = ['free', 'telegram', 'regular', 'premium', 'privileged', 'admin'] as const;
export type Tier = (typeof TIERS)[number];

export type Feature =
  | 'dashboard' | 'traders' | 'strategy_builder' | 'copy_paper' | 'sessions' | 'learn' | 'backtest'
  | 'copy_telegram'
  | 'copy_live' | 'broker_keys' | 'community' | 'ai_advisor' | 'orb_unlimited'
  | 'ai_trader' | 'order_priority'
  | 'admin_activity';

export function isTier(v: unknown): v is Tier {
  return typeof v === 'string' && (TIERS as readonly string[]).includes(v);
}

/** The first tier that gets a feature. */
export function requiredTier(feature: Feature): Tier {
  const t = file.features[feature];
  // A feature missing from the file is the most restricted, never open by mistake.
  return isTier(t) ? t : 'admin';
}

export function tierRank(t: Tier): number {
  return TIERS.indexOf(t);
}

export function tierAllows(tier: Tier, feature: Feature): boolean {
  return tierRank(tier) >= tierRank(requiredTier(feature));
}

/** Orb questions a day for a tier; null = unlimited or not decided yet. */
export function orbDailyQuestions(tier: Tier): number | null {
  if (tierAllows(tier, 'orb_unlimited')) return null;
  const n = file.orb_daily_questions[tier];
  return typeof n === 'number' ? n : null;
}

/** Translation key (common namespace) of a tier's name. */
export const tierNameKey = (t: Tier) => `tier.name.${t}`;

/** The file's own lists, for the test that keeps this in step with it. */
export const TIER_FILE = file;
